import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FaTrashAlt } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";

import { CourseExamApi } from "api/course-exams/courseExamsApi";
import {
  PaziaButton,
  PaziaErrorMessage,
  PaziaFormButton,
  PaziaInput,
  PaziaMultiSelect,
  PaziaSelect,
} from "components/atoms";
import { ExamOption, useExamOptions, useCourseSessionsOptions, useTrainersOptions } from "api/hooks";
import { PaziaPrefixInput } from "components/atoms/Inputs";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { BaseScheduleForm, ScheduleFormBase } from "pages/CourseCreate/Schedule/create-new/BaseScheduleForm";
import { BasePlaceScheduleForm, ScheduleFormPlace } from "pages/CourseCreate/Schedule/BasePlaceScheduleForm";

interface ExamFormProps {
  onSubmit: (data: ExamFormValues) => Promise<void>;
  courseId: number;
  exam?: CourseExamApi;
  onCancel?: () => void;
}

export const ExamForm = ({ onSubmit, onCancel, exam, courseId }: ExamFormProps): JSX.Element => {
  const { loading, options } = useExamOptions();
  const { loading: loadingSessions, options: sessionsOptions } = useCourseSessionsOptions(courseId);
  const { isLoading: loadingTrainers, options: trainerOptions } = useTrainersOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.ID]: exam?.id,
      [FORM_NAMES.EXAM_ID]: exam?.examId,
      [FORM_NAMES.NAME]: exam?.name,
      [FORM_NAMES.DESCRIPTION]: exam?.description,
      [FORM_NAMES.TRAINER_IDS]: exam?.trainers?.map((t) => t.trainerId.toString()) ?? [],
      [FORM_NAMES.PAYMENTS]: exam?.payments ?? [{}],
      ...ScheduleFormBase.resolveDefaultValues({
        session: { id: exam?.courseSessionId as number },
        dateFrom: exam?.day as Date,
        timeFrom: exam?.timeFrom as string,
        timeTo: exam?.timeTo as string,
      }),
      ...ScheduleFormPlace.resolveDefaultValues({
        placeType: exam?.placeType as number,
        placeId: exam?.placeId,
      }),
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const {
    fields: paymentFields,
    append: appendPayment,
    remove: removePayment,
  } = useFieldArray({
    control: methods.control,
    name: FORM_NAMES.PAYMENTS,
    keyName: "key",
  });

  const onExamSelect = (opt: ExamOption) => setValue(FORM_NAMES.NAME, opt?.exam?.name ?? "");
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2">
          <PaziaSelect
            label="Egzamin"
            noOptionsMessage="Brak egzaminów — utwórz je w module Egzaminy"
            name={FORM_NAMES.EXAM_ID}
            required
            options={options}
            isLoading={loading}
            onChange={onExamSelect}
          />
          <PaziaInput name={FORM_NAMES.NAME} label="Nazwa" required />
          <BaseScheduleForm sessionsOptions={sessionsOptions} />
          <BasePlaceScheduleForm />
          <PaziaMultiSelect
            name={FORM_NAMES.TRAINER_IDS}
            label="Trenerzy"
            options={trainerOptions}
            isLoading={loadingTrainers || loadingSessions}
          />
          <PaziaFromQuill name={FORM_NAMES.DESCRIPTION} label="Opis" />
          <div className="flex flex-col gap-2">
            <span className="mb block truncate text-sm font-medium">
              Płatności<span className="ml-1 text-rose-500">*</span>
            </span>
            {paymentFields.map((x, index) => (
              <div className="flex items-end gap-2" key={x.key}>
                <PaziaPrefixInput
                  prefix="PLN"
                  placeholder="Cena"
                  name={`${FORM_NAMES.PAYMENTS}[${index}].price`}
                  label={index === 0 ? "Cena" : undefined}
                />
                <PaziaInput
                  placeholder="Ilość prób"
                  label={index === 0 ? "Ilość prób" : undefined}
                  name={`${FORM_NAMES.PAYMENTS}[${index}].attempts`}
                  type="number"
                  tooltip="Wpisz 0 dla nieskończonej ilości prób."
                />
                <div className="mb-[0.7rem] flex gap-2">
                  <FaTrashAlt
                    onClick={() => {
                      if (paymentFields.length > 1) removePayment(index);
                    }}
                    size={16}
                    className={iconClass}
                  />
                  <FaPlus size={16} onClick={() => appendPayment({})} className={iconClass} />
                </div>
              </div>
            ))}
          </div>
        </div>
        {errors.payments && paymentFields.length === 0 && (
          <PaziaErrorMessage>Dodaj przynajmniej jedną płatność</PaziaErrorMessage>
        )}
        <div className="mt-4 flex justify-end gap-2">
          {onCancel && <PaziaButton type="tertiary" title="Anuluj" onClick={onCancel} btnType="button" />}
          <PaziaFormButton title={exam?.id ? "Zapisz zmiany" : "Utwórz egzamin"} type="success" />
        </div>
      </form>
    </FormProvider>
  );
};
const iconClass = "text-slate-400 hover:text-black cursor-pointer";

const FORM_NAMES = {
  ID: "id",
  EXAM_ID: "examId",
  NAME: "name",
  DESCRIPTION: "description",
  PAYMENTS: "payments",
  TRAINER_IDS: "trainerIds",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.ID]: yup.number().nullable(),
    [FORM_NAMES.EXAM_ID]: yup.number().required(),
    [FORM_NAMES.NAME]: yup.string().required(),
    [FORM_NAMES.DESCRIPTION]: yup.string(),
    [FORM_NAMES.TRAINER_IDS]: yup.array().of(yup.string()).nullable(),
    [FORM_NAMES.PAYMENTS]: yup
      .array()
      .of(
        yup.object({
          id: yup.number().nullable(),
          price: yup.number().nullable(),
          attempts: yup.number().nullable(),
        }),
      )
      .required()
      .min(1, "Musisz dodać przynajmniej jedną płatność"),
  })
  .concat(ScheduleFormBase.schema)
  .concat(ScheduleFormPlace.schema)
  .required();

export type ExamFormValues = yup.InferType<typeof schema>;
